import { createContext, useContext, useState, useEffect } from 'react';

const SiteContext = createContext();

export function SiteProvider({ children, user }) {
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!user) return;

    const fetchSites = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sites`, {
          credentials: 'include'
        });
        if (response.ok) {
          const sitesData = await response.json();
          setSites(sitesData);
          
          // Set initial selected site
          if (user.role === 'super_admin') {
            // iter219 — default to the super admin's OWN workshop (not
            // "all_sites"): the all-sites default made the Job Cards billed
            // target sum mechanics across EVERY workshop (38 → 50 when staff
            // were added to another site). Last selection is remembered.
            let initial = null;
            try { initial = localStorage.getItem('asf_selected_site_id'); } catch { /* noop */ }
            const isValid = initial && (initial === 'all_sites' || sitesData.some(s => s.site_id === initial));
            setSelectedSiteId(isValid ? initial : (user.site_id || 'all_sites'));
          } else {
            setSelectedSiteId(user.site_id); // Non-super-admin: use assigned site
          }
        }
      } catch (error) {
        console.error('Failed to fetch sites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [user, BACKEND_URL]);

  const getSiteParams = () => {
    // For super admin, return site_id param if not viewing all
    if (user?.role === 'super_admin' && selectedSiteId && selectedSiteId !== 'all_sites') {
      return `?site_id=${selectedSiteId}`;
    }
    if (user?.role === 'super_admin' && selectedSiteId === 'all_sites') {
      return '?site_id=all_sites';
    }
    // For non-super-admin, site filtering happens server-side
    return '';
  };

  const selectSite = (siteId) => {
    setSelectedSiteId(siteId);
    try { localStorage.setItem('asf_selected_site_id', siteId); } catch { /* noop */ }
  };

  const value = {
    selectedSiteId,
    setSelectedSiteId: selectSite,
    sites,
    loading,
    getSiteParams,
    isSuperAdmin: user?.role === 'super_admin'
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
}